import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { ButtonComponent } from '../../../button/button.component';
import { TranslocoDirective } from '@jsverse/transloco';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { Country, CountryService } from '../../../../services/country.service';
import { AvailabilityService, Warehouse } from './availability.service';
import { DropdownComponent } from '../../../dropdown/dropdown.component';
import { DropdownOption } from '../../product.types';
import { combineLatest, map, Observable, of, ReplaySubject, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ShowroomAvailabilityComponent } from './showroom-availability/showroom-availability.component';
import { AsyncPipe } from '@angular/common';
import { Store, StoresService } from '../../../stores-map/stores.service';

@Component({
  selector: 'app-availability-at-showrooms',
  imports: [
    ButtonComponent,
    TranslocoDirective,
    SvgIconComponent,
    DropdownComponent,
    ShowroomAvailabilityComponent,
    AsyncPipe,
  ],
  templateUrl: './availability-at-showrooms.component.html',
  styleUrls: ['./availability-at-showrooms.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvailabilityAtShowroomsComponent implements OnInit {
  private countryService = inject(CountryService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private availability = inject(AvailabilityService);
  private stores = inject(StoresService);
  private warehouses: Warehouse[] | null = null;
  private countryChange$ = new ReplaySubject<Country>(1);

  countriesAsDropdown: WritableSignal<DropdownOption<Country>[]> = signal([]);

  selectedCountry: Country | null = null;
  dataLoaded = signal(false);

  availabilityResult$?: Observable<(Store & Pick<Warehouse, 'stocks'>)[] | null>;

  showrooms: Store[] | null = null;

  @Input({ required: true })
  sku!: string;

  @Output()
  clicked = new EventEmitter();

  ngOnInit(): void {
    this.stores.get().subscribe((stores) => {
      this.showrooms = stores;
      this.cdr.markForCheck();
    });
  }

  init(): void {
    if (!this.showrooms) {
      return;
    }

    this.countryService
      .getAvailableCountriesAsDropdown()
      .pipe(
        map((options) => {
          return options.filter((o) => {
            const countryCode = o.value.key.toLowerCase();

            if (!this.showrooms || countryCode === 'pl') {
              return;
            }

            const showroomList = this.showrooms.filter(
              (showroom) => showroom.country.toLowerCase() === countryCode,
            );

            return showroomList.length > 0;
          });
        }),
      )
      .subscribe((options) => {
        this.countriesAsDropdown.set(options);
      });

    this.availabilityResult$ = combineLatest([
      this.countryChange$,
      this.getAvailability(),
    ]).pipe(
      map(([country, availability]: [Country, Warehouse[] | null]) => {
        if (!availability || !this.showrooms) {
          return null;
        }

        const showroomsInCountry = this.showrooms.filter(
          (showroom) => showroom.country.toLowerCase() === country.key.toLowerCase(),
        );

        return showroomsInCountry.map((showroom) => {
          const warehouse = availability.find((a) => a.warehouse.id === showroom.erp_id);

          return {
            ...showroom,
            stocks: warehouse?.stocks ?? [],
          };
        });
      }),
      tap(() => this.dataLoaded.set(true)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  onCountryChange(country: Country | null): void {
    if (!country) {
      return;
    }

    this.selectedCountry = country;
    this.countryChange$.next(country);
  }

  getAvailability(): Observable<Warehouse[] | null> {
    if (this.warehouses) {
      return of(this.warehouses);
    }

    return this.availability.get(this.sku).pipe(tap((w) => (this.warehouses = w)));
  }
}
